import React, { useState, useEffect } from 'react';
import {
    Typography,
    Box,
    Card,
    CardContent,
    TextField,
    Autocomplete,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    LinearProgress,
    Alert,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    Stack,
} from '@mui/material';
import { LocationOn, Add, Delete, CheckCircle, Warning, Print } from '@mui/icons-material';
import { DebundleBundleInfo, ChildSKUAllocation } from './types';
import { WarehouseAPI } from '../../services/api';
import { useToast } from '../common/useToast';
import {
    allocatedForSKU,
    applyAllocation,
    getProgressForSKU as computeProgressForSKU,
    allAllocationsComplete,
} from '../../utils/debundleAllocation';

interface ShelfWithInventory {
    shelfCode: string;
    existingQuantity: number;
}

interface DebundleChildSKUAllocatorProps {
    selectedBundle: DebundleBundleInfo;
    quantity: number;
    allocations: ChildSKUAllocation[];
    onAllocationsChange: (allocations: ChildSKUAllocation[]) => void;
}

const DebundleChildSKUAllocator: React.FC<DebundleChildSKUAllocatorProps> = ({
    selectedBundle,
    quantity,
    allocations,
    onAllocationsChange,
}) => {
    const toast = useToast();
    const [selectedSKU, setSelectedSKU] = useState<string | null>(null);
    const [selectedShelf, setSelectedShelf] = useState<string>('');
    const [allocationQuantity, setAllocationQuantity] = useState<number>(1);
    const [shelvesWithInventory, setShelvesWithInventory] = useState<ShelfWithInventory[]>([]);
    const [isLoadingShelfInventory, setIsLoadingShelfInventory] = useState<boolean>(false);

    // Print options state
    const [includeBundleName, setIncludeBundleName] = useState(true);
    const [includePickerName, setIncludePickerName] = useState(true);
    const [pickerName, setPickerName] = useState<string>(() => {
        try {
            const last = window.localStorage.getItem('lastPickerName');
            if (last) return last;
            const stored = window.localStorage.getItem('bundleManagerUser');
            if (stored) return JSON.parse(stored).name || '';
        } catch {
            // ignore parse errors
        }
        return '';
    });

    // Fetch shelf inventory for the selected child SKU
    useEffect(() => {
        if (!selectedSKU) {
            setShelvesWithInventory([]);
            return;
        }

        const fetchShelfInventory = async () => {
            setIsLoadingShelfInventory(true);
            try {
                const response = await WarehouseAPI.getShelfWiseInventory(selectedSKU, 0, 1000);

                // Filter for GOOD_INVENTORY items that match the SKU
                const shelves: ShelfWithInventory[] = response.data
                    .filter(
                        (item: any) =>
                            item.skuCode.toLowerCase() === selectedSKU.toLowerCase() &&
                            item.inventoryType === 'GOOD_INVENTORY' &&
                            item.quantity > 0
                    )
                    .map((item: any) => ({
                        shelfCode: item.shelfCode,
                        existingQuantity: item.quantity,
                    }));

                setShelvesWithInventory(shelves);
            } catch (error) {
                console.error('Error fetching shelf inventory for child SKU:', error);
                setShelvesWithInventory([]);
            } finally {
                setIsLoadingShelfInventory(false);
            }
        };

        fetchShelfInventory();
    }, [selectedSKU]);

    // Generate shelf options with inventory shelves at top
    const getShelfOptionsForSelectedSKU = () => {
        // Explicit shelf options as requested by the user
        const baseOptions = [
            'B2B-BLINKIT', 'B2B-CONSUMPTION', 'B2B-FBA', 'B2B-KLYDO', 'B2B-KNOT',
            'B2B-MYNTRA', 'B2B-SLIKK', 'B2B-ZEPTO', 'B2B-ZILO', 'BLINKIT',
            'BLINKIT-NC', 'blocked_bin', 'CARDING', 'CARDING-CONSUMPTION',
            'D2C-CONSUMPTION', 'D2C-GB', 'DEFAULT',
            'GF001-MIX_RTO', 'GF002-MIX_RTO', 'GF003-MIX_RTO', 'GF004-MIX_RTO', 'GF005-MIX_RTO',
            'LOVEBOX-INSERT', 'MNEW',
            'NCNEW', 'NECKLACE-CARD', 'NON-CARDED',
            'NONCARDED-1', 'NONCARDED-2', 'NONCARDED-3', 'NONCARDED-4', 'NONCARDED-5',
            'NONCARDED-6', 'NONCARDED-7', 'NONCARDED-8', 'NONCARDED-9', 'NONCARDED-10',
            'NONCARDED-11', 'NONCARDED-12', 'NONCARDED-13', 'NONCARDED-14', 'NONCARDED-15',
            'NONCARDED-16', 'NONCARDED-17', 'NONCARDED-18', 'NONCARDED-19', 'NONCARDED-20',
            'NOT_FOUND', 'PA1',
            'PA10', 'PA2', 'PA3', 'PA4', 'PA5', 'PA6', 'PA7', 'PA8', 'PA9',
            'PACKAGING', 'PB1', 'PB10', 'PB2', 'PB3', 'PB4', 'PB5', 'PB6',
            'PB7', 'PB8', 'PB9', 'PC1', 'PC10', 'PC11', 'PC12', 'PC2', 'PC3',
            'PC4', 'PC5', 'PC6', 'PC7', 'PC8', 'PC9', 'PR1', 'PR2', 'pr3',
            'pr4', 'pr5', 'pr6', 'pr7', 'RAKHI', 'RAKHI-1', 'RAKHI-2', 'RAKHI-3',
            'RAKHI-4', 'RAKHI-5', 'RAKHI-6', 'RAKHI-7', 'RAKHI-8', 'RAKHI-9',
            'SH001', 'SH002', 'SH003',
            'SH004', 'SH005', 'SH006', 'SH007', 'SH008', 'SH009', 'SH010',
            'SH011', 'SH012', 'SH013', 'SH014', 'SH015', 'SH016', 'SH017',
            'SH018', 'SH019', 'SH020', 'SH021', 'SH022', 'SH023', 'SH024',
            'SH025', 'SH026', 'SH027', 'SH028', 'SH029', 'SH030', 'SH031',
            'SH032', 'SH033', 'SH034', 'SH035', 'SH036', 'SH037', 'SH038',
            'SH039', 'SH040', 'SH041', 'SH042', 'SH043', 'SH044', 'SH045',
            'SH046', 'SH047', 'SH048', 'SH049', 'SH050', 'SH051', 'SH052',
            'SH053', 'SH054', 'SH055', 'SH056', 'SH057', 'SH058', 'SH059',
            'SH060', 'SH061', 'SH062', 'SH063', 'SH064', 'SH065', 'SH066',
            'SH067', 'SH068', 'SH069', 'SH070', 'SH071', 'SH072', 'SH073',
            'SH074', 'SH075', 'SH076', 'SH077', 'SH078', 'SH079', 'SH080',
            'SH081', 'SH082', 'SH083', 'SH084', 'SH085', 'SH086', 'SH087',
            'SH088', 'SH089', 'SH090', 'SH091', 'SH092', 'SH093', 'SH094',
            'SH095', 'SH096', 'SH097', 'SH098', 'SH099', 'SH100', 'SH101',
            'sh102', 'SH103', 'SH104', 'SH105', 'SH106', 'SH107', 'SH108',
            'SH109', 'SH110', 'SH112', 'sh113', 'sh114', 'sh115', 'Sh116',
            'SH117', 'SH118', 'SH119', 'SH120', 'SH121', 'SH122', 'SH123',
            'SH124', 'SH125', 'SH126', 'SH127', 'SH128', 'SH129', 'SH130',
            'SH131', 'SH132', 'SH133', 'SH134', 'SH135', 'SH136', 'SH137',
            'SH138', 'SH139', 'SH140', 'SH141', 'SH142', 'SH143', 'SH144',
            'SH145', 'SH146', 'SH147', 'SH148', 'SH149', 'SH150', 'SH151',
            'SH152', 'SH153', 'SH154', 'SH155', 'SH156', 'SH157', 'SH158',
            'SH159', 'SH160', 'SH161', 'SH162', 'SH163', 'SH164', 'SH165',
            'SH166', 'SH167', 'SH168', 'SH169', 'SH170', 'SH171', 'SH172',
            'SH173', 'SH174', 'SH175', 'SH176', 'SH177', 'SH178', 'SH179',
            'SH180', 'SH181', 'SH182', 'SH183', 'SH184', 'SH185', 'SH186',
            'SH187', 'SH188', 'SH189', 'SH190', 'SH191', 'SH192', 'SH193',
            'SH194', 'SH195', 'SH196', 'SH197', 'SH198', 'SH199', 'SH200',
            'SH201', 'SH202', 'SH203', 'SH204', 'SH205', 'SH206', 'SH207',
            'SH208', 'SH209', 'SH210', 'SH211', 'SH212', 'SH213', 'SH214',
            'SH215', 'SH216', 'SH217', 'SH218', 'SH219', 'SH220', 'SHOO5',
            'TEST', 'TEST2', 'VS001', 'VS002', 'VS003', 'VS004', 'VS005',
            'VS006', 'VS007', 'VS008', 'VS009', 'VS010', 'VS011', 'VS012',
            'VS013', 'VS014', 'VS015', 'VS016', 'VS017', 'VS018', 'VS019',
            'VS020', 'VS021', 'VS022', 'VS023', 'VS024', 'VS025', 'VS026',
            'VS027', 'VS028', 'VS029', 'VS030', 'VS031', 'VS032', 'VS033',
            'VS034', 'VS035', 'VS036', 'VS037', 'VS038', 'VS039', 'VS040',
            'VS041', 'VS042', 'VS043', 'VS044', 'VS045', 'VS046', 'VS047',
            'VS048', 'VS049', 'VS050', 'VS051', 'VS052', 'VS053', 'VS054',
            'VS055', 'VS056', 'VS057', 'VS058', 'VS059', 'VS060', 'VS061',
            'VS062', 'VS063', 'VS064', 'VS065', 'VS066', 'VS067', 'VS068',
            'VS069', 'VS070', 'VS071', 'VS072', 'VS073', 'VS074', 'VS075',
            'VS076', 'VS077', 'VS078', 'VS079', 'VS080', 'VS081', 'VS082',
            'VS083', 'VS084', 'VS085', 'VS086', 'VS087', 'VS088', 'VS089',
            'VS090', 'VS091', 'VS092', 'VS093', 'VS094', 'VS095', 'VS096',
            'VS097', 'VS098', 'VS099', 'VS100', 'WH-PHOTOSHOOT'
        ];

        // Create a map of existing inventory
        const inventoryMap = new Map<string, number>();
        shelvesWithInventory.forEach(shelf => {
            inventoryMap.set(shelf.shelfCode, shelf.existingQuantity);
        });

        // Create options with shelves that have inventory first
        const shelvesWithStock = shelvesWithInventory
            .map(shelf => ({
                label: `${shelf.shelfCode} (${shelf.existingQuantity})`,
                value: shelf.shelfCode,
                hasInventory: true,
                quantity: shelf.existingQuantity,
            }))
            .sort((a, b) => b.quantity - a.quantity); // Sort by quantity descending

        const shelvesWithoutStock = baseOptions
            .filter(shelfCode => !inventoryMap.has(shelfCode))
            .map(shelfCode => ({
                label: shelfCode,
                value: shelfCode,
                hasInventory: false,
                quantity: 0,
            }))
            .sort((a, b) => a.value.localeCompare(b.value));

        return [...shelvesWithStock, ...shelvesWithoutStock];
    };

    const shelfOptions = getShelfOptionsForSelectedSKU();

    const getAvailableSKUs = () => {
        return allocations.filter((sku) => {
            return allocatedForSKU(sku) < sku.totalQuantity;
        }).map((sku) => ({
            label: `${sku.name} (${sku.totalQuantity - allocatedForSKU(sku)} remaining)`,
            value: sku.skuId,
        }));
    };

    const getProgressForSKU = (skuId: string) => {
        const sku = allocations.find((s) => s.skuId === skuId);
        return computeProgressForSKU(sku);
    };

    const handleAddAllocation = () => {
        if (!selectedSKU || !selectedShelf.trim() || allocationQuantity <= 0) return;

        const result = applyAllocation(allocations, selectedSKU, selectedShelf, allocationQuantity);
        if (!result.ok) {
            // Rejection only surfaces a toast for the over-allocation case (the other guards are
            // already covered by the early return + disabled Add button above).
            if (allocationQuantity > result.remaining) {
                toast.warning(`Cannot allocate more than ${result.remaining} units`);
            }
            return;
        }

        onAllocationsChange(result.allocations);
        setSelectedSKU(null);
        setSelectedShelf('');
        setAllocationQuantity(1);
    };

    const handleRemoveAllocation = (skuId: string, shelfLocation: string) => {
        const newAllocations = allocations.map((sku) => {
            if (sku.skuId === skuId) {
                return {
                    ...sku,
                    allocations: sku.allocations.filter((a) => a.shelfLocation !== shelfLocation),
                };
            }
            return sku;
        });
        onAllocationsChange(newAllocations);
    };

    // Print pick list: Child SKU, Target Shelf and Quantity
    const handlePrintPickList = () => {
        const rows: Array<{ sku: string; shelf: string; qty: number }> = [];

        allocations.forEach((sku) => {
            if (sku.allocations && sku.allocations.length > 0) {
                sku.allocations.forEach((allocation) => {
                    if (allocation.quantity && allocation.quantity > 0) {
                        rows.push({
                            sku: sku.name,
                            shelf: allocation.shelfLocation,
                            qty: allocation.quantity,
                        });
                    }
                });
            }
        });

        if (rows.length === 0) {
            toast.warning('No allocations to print.');
            return;
        }

        const titleParts: string[] = [];
        if (includeBundleName) titleParts.push(selectedBundle.name);
        if (includePickerName) {
            const storedName = (() => {
                try {
                    const stored = window.localStorage.getItem('bundleManagerUser');
                    if (stored) return JSON.parse(stored).name || '';
                } catch {
                    return '';
                }
                return '';
            })();
            titleParts.push((pickerName && pickerName.trim()) ? pickerName.trim() : storedName || '');
        }
        const title = `Debundle Put List${titleParts.length ? ' - ' + titleParts.filter(Boolean).join(' - ') : ''} - ${new Date().toLocaleString()}`;

        const style = `
            <style>
                body { font-family: Arial, Helvetica, sans-serif; padding: 24px; }
                h1 { font-size: 18px; margin-bottom: 8px; }
                table { width: 100%; border-collapse: collapse; margin-top: 12px; }
                th, td { border: 1px solid #333; padding: 8px; text-align: left; }
                th { background: #f2f2f2; }
            </style>
        `;

        const rowsHtml = rows.map(r => `
            <tr>
                <td>${r.sku}</td>
                <td>${r.shelf}</td>
                <td style="text-align: right">${r.qty}</td>
            </tr>
        `).join('\n');

        const metaHtmlParts: string[] = [];
        if (includeBundleName) metaHtmlParts.push(`<div><strong>Bundle:</strong> ${selectedBundle.name}</div>`);
        metaHtmlParts.push(`<div><strong>Debundle Quantity:</strong> ${quantity}</div>`);
        if (includePickerName) {
            const storedName = (() => {
                try {
                    const stored = window.localStorage.getItem('bundleManagerUser');
                    if (stored) return JSON.parse(stored).name || '';
                } catch {
                    return '';
                }
                return '';
            })();
            metaHtmlParts.push(`<div><strong>Picker:</strong> ${(pickerName && pickerName.trim()) ? pickerName.trim() : storedName || 'N/A'}</div>`);
        }
        metaHtmlParts.push(`<div><strong>Printed At:</strong> ${new Date().toLocaleString()}</div>`);

        const html = `
            <html>
                <head>
                    <title>${title}</title>
                    ${style}
                </head>
                <body>
                    <h1>${title}</h1>
                    <div style="margin-bottom:12px;">${metaHtmlParts.join('')}</div>
                    <table>
                        <thead>
                            <tr>
                                <th>Child SKU</th>
                                <th>Put to Shelf</th>
                                <th>Quantity</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${rowsHtml}
                        </tbody>
                    </table>
                </body>
            </html>
        `;

        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            toast.warning('Popup blocked. Please allow popups to print the put list.');
            return;
        }

        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
        // Give browser a moment to render before printing
        setTimeout(() => {
            printWindow.print();
        }, 300);
    };

    const allComplete = allAllocationsComplete(allocations);

    return (
        <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn />
                Allocate Child SKUs to Shelf Locations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Specify where each returned child SKU should be placed. Shelves with existing inventory are shown at the top.
            </Typography>

            {/* Add Allocation Form */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Add />
                        Add Shelf Allocation
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <Autocomplete
                            value={selectedSKU ? getAvailableSKUs().find((s) => s.value === selectedSKU) || null : null}
                            onChange={(_, newValue) => {
                                setSelectedSKU(newValue?.value || null);
                                setSelectedShelf(''); // Reset shelf when SKU changes
                            }}
                            options={getAvailableSKUs()}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value?.value}
                            renderInput={(params) => (
                                <TextField {...params} label="Select Child SKU" size="small" sx={{ minWidth: 250 }} />
                            )}
                            sx={{ flexGrow: 1 }}
                        />

                        <Autocomplete
                            value={shelfOptions.find(opt => opt.value === selectedShelf) || null}
                            onChange={(_, newValue) => setSelectedShelf(newValue?.value || '')}
                            options={shelfOptions}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value?.value}
                            groupBy={(option) => option.hasInventory ? '📦 Shelves with existing inventory' : '📍 Other shelves'}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Target Shelf"
                                    size="small"
                                    sx={{ minWidth: 200 }}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {isLoadingShelfInventory ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            renderOption={(props, option) => (
                                <li {...props} key={option.value}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                        <span>{option.value}</span>
                                        {option.hasInventory && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: 'success.main',
                                                    fontWeight: 'bold',
                                                    ml: 2
                                                }}
                                            >
                                                ({option.quantity} in stock)
                                            </Typography>
                                        )}
                                    </Box>
                                </li>
                            )}
                            disabled={!selectedSKU || isLoadingShelfInventory}
                        />

                        <TextField
                            label="Quantity"
                            type="number"
                            value={allocationQuantity}
                            onChange={(e) => setAllocationQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                            size="small"
                            sx={{ width: 100 }}
                            inputProps={{ min: 1 }}
                            disabled={!selectedSKU}
                        />

                        <Button
                            variant="contained"
                            onClick={handleAddAllocation}
                            disabled={!selectedSKU || !selectedShelf.trim() || allocationQuantity <= 0}
                            startIcon={<Add />}
                        >
                            Add
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Progress Overview */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                        Allocation Progress
                    </Typography>

                    {allocations.map((sku) => {
                        const progress = getProgressForSKU(sku.skuId);
                        const allocated = allocatedForSKU(sku);
                        const isComplete = allocated === sku.totalQuantity;

                        return (
                            <Box key={sku.skuId} sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" fontWeight="medium">
                                        {sku.name}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {allocated} / {sku.totalQuantity}
                                        </Typography>
                                        {isComplete ? (
                                            <CheckCircle color="success" fontSize="small" />
                                        ) : (
                                            <Warning color="warning" fontSize="small" />
                                        )}
                                    </Box>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={progress}
                                    color={isComplete ? 'success' : 'primary'}
                                    sx={{ height: 6, borderRadius: 3 }}
                                />
                            </Box>
                        );
                    })}
                </CardContent>
            </Card>

            {/* Allocations Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Child SKU</TableCell>
                            <TableCell>Target Shelf</TableCell>
                            <TableCell align="right">Quantity</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {allocations.map((sku) =>
                            sku.allocations.length === 0 ? (
                                <TableRow key={sku.skuId}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">{sku.name}</Typography>
                                    </TableCell>
                                    <TableCell colSpan={3}>
                                        <Typography variant="caption" color="text.secondary">
                                            No allocations yet. Total needed: {sku.totalQuantity}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sku.allocations.map((allocation, index) => (
                                    <TableRow key={`${sku.skuId}-${allocation.shelfLocation}`}>
                                        {index === 0 && (
                                            <TableCell rowSpan={sku.allocations.length}>
                                                <Typography variant="body2" fontWeight="medium">{sku.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Total: {sku.totalQuantity}
                                                </Typography>
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LocationOn color="action" fontSize="small" />
                                                {allocation.shelfLocation}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">{allocation.quantity}</TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                onClick={() => handleRemoveAllocation(sku.skuId, allocation.shelfLocation)}
                                                color="error"
                                                size="small"
                                            >
                                                <Delete />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Status Alert */}
            <Box sx={{ mt: 3 }}>
                <Alert severity={allComplete ? 'success' : 'warning'}>
                    {allComplete
                        ? 'All child SKUs have been fully allocated. You can proceed to the next step.'
                        : 'Please allocate all child SKUs to shelf locations before proceeding.'}
                </Alert>
            </Box>

            {/* Print Put List Section - Only show when all allocations are complete */}
            {allComplete && (
                <Card sx={{ mt: 3, bgcolor: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                            <Print sx={{ color: 'white' }} />
                            Print Put List for Picker
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255,255,255,0.7)' }}>
                            Print a list of child SKUs and their target shelf locations for the picker to execute.
                        </Typography>

                        <Stack direction="row" spacing={2} alignItems="center">
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<Print />}
                                onClick={handlePrintPickList}
                                sx={{
                                    minWidth: 180,
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: 'primary.dark',
                                    }
                                }}
                            >
                                Print Put List
                            </Button>
                        </Stack>

                        <Stack direction="row" spacing={2} sx={{ mt: 2, alignItems: 'center' }}>
                            <FormControlLabel
                                control={<Checkbox checked={includeBundleName} onChange={(e) => setIncludeBundleName(e.target.checked)} sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: 'primary.main' } }} />}
                                label={<Typography sx={{ color: 'rgba(255,255,255,0.9)' }}>Include bundle name</Typography>}
                            />
                            <FormControlLabel
                                control={<Checkbox checked={includePickerName} onChange={(e) => setIncludePickerName(e.target.checked)} sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: 'primary.main' } }} />}
                                label={<Typography sx={{ color: 'rgba(255,255,255,0.9)' }}>Include picker name</Typography>}
                            />
                            {includePickerName && (
                                <TextField
                                    size="small"
                                    label="Picker name"
                                    value={pickerName}
                                    onChange={(e) => {
                                        setPickerName(e.target.value);
                                        try { window.localStorage.setItem('lastPickerName', e.target.value); } catch { };
                                    }}
                                    sx={{
                                        minWidth: 200,
                                        '& .MuiInputBase-root': {
                                            bgcolor: 'rgba(255,255,255,0.1)',
                                            color: 'white',
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: 'rgba(255,255,255,0.7)',
                                            '&.Mui-focused': { color: 'primary.main' },
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(255,255,255,0.3)',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(255,255,255,0.5)',
                                        },
                                    }}
                                />
                            )}
                        </Stack>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default DebundleChildSKUAllocator;
